import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { plannerConfigService } from '../../services/plannerConfigService';
import { plannerStorageService } from '../../services/plannerStorageService';
import { fontService } from '../../services/fontService';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PlannerTypeSelector from './components/PlannerTypeSelector';
import SizeFormatSelector from './components/SizeFormatSelector';
import ColorThemeSelector from './components/ColorThemeSelector';
import LayoutCustomizer from './components/LayoutCustomizer';
import PersonalizationOptions from './components/PersonalizationOptions';
import PlannerPreview from './components/PlannerPreview';
import PlannerGenerationModal from '../../components/ui/PlannerGenerationModal';
import { generatePlannerPDF } from '../../utils/pdfGenerator';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getSessionBackground } from '../../utils/backgroundRotation';

// Configure PDF.js worker at top of file (after imports)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs?.version}/build/pdf.worker.min.mjs`;

const PlannerSetupWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Get initial step from URL params
  const urlParams = new URLSearchParams(location.search);
  const initialStep = parseInt(urlParams?.get('step')) || 1;

  // Wizard state
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentConfigId, setCurrentConfigId] = useState(null);

  // Add preview state management
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Add state for full PDF generation
  const [fullPdfUrl, setFullPdfUrl] = useState(null);
  const [isGeneratingFull, setIsGeneratingFull] = useState(false);
  const [fullPdfGenerated, setFullPdfGenerated] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);

  // Add PDF loading state
  const [numPages, setNumPages] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);

  // Add background image state
  const [backgroundImage, setBackgroundImage] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    plannerType: '',
    size: '',
    theme: '',
    layout: '',
    timeFormat: '12h-long',
    timeRange: {
      start: 6,
      end: 18
    },
    sections: [],
    habits: [],
    personalization: {
      name: '',
      year: new Date()?.getFullYear(),
      quote: '',
      font: 'helvetica',
      titleFont: null,
      backgroundText: '',
      coverBackgroundImage: null, // NEW: Add field for selected cover background
      includeCover: true,
      includeCalendar: true,
      includeGoals: false,
      includePageNumbers: true,
      includeIndex: false,
      includeBottomQuotes: false,
      quoteFrequency: 'daily',
      selectedQuoteSet: 'motivational'
    }
  });

  // Add state for available fonts
  const [availableFonts, setAvailableFonts] = useState([]);

  // Add state for storage upload
  const [storageUploadStatus, setStorageUploadStatus] = useState({
    inProgress: false,
    success: false,
    error: null,
    storageUrl: null
  });

  // Add helper function to safely convert values to strings for display
  const safeStringValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value?.[0] ? String(value?.[0]) : '';
    if (typeof value === 'object' && value !== null) {
      return value?.value || value?.id || value?.name || value?.label || '';
    }
    try {
      return String(value);
    } catch (e) {
      return '';
    }
  };

  // NEW: Helper function to extract human-readable Type summary from layout object
  const getTypeSummary = (layout) => {
    if (!layout || typeof layout !== 'object') return 'Not selected';

    const components = [];

    // Check main components
    if (layout?.yearlyOverview) components?.push('Yearly Overview');
    if (layout?.monthPlanner) components?.push('Monthly Planner');
    if (layout?.weekPlanner) components?.push('Weekly Planner');
    if (layout?.dailyPlanner?.enabled) components?.push('Daily Planner');

    return components?.length > 0 ? components?.join(', ') : 'Not selected';
  };

  // NEW: Helper function to extract human-readable Layout summary from layout object
  const getLayoutSummary = (layout) => {
    if (!layout || typeof layout !== 'object') return 'Not selected';

    const layoutDetails = [];

    // Daily Planner layout details
    if (layout?.dailyPlanner?.enabled) {
      if (layout?.dailyPlanner?.schedule) {
        layoutDetails?.push('Schedule Only');
      } else if (layout?.dailyPlanner?.task) {
        layoutDetails?.push('Task Only');
      } else if (layout?.dailyPlanner?.combination) {
        layoutDetails?.push('Schedule + Task Combination');
      }

      // Journal details
      if (layout?.dailyPlanner?.journal?.enabled) {
        if (layout?.dailyPlanner?.journal?.type === 'subject_headings') {
          layoutDetails?.push('Journal with Subject Headings');
        } else {
          layoutDetails?.push('Journal (Free-form)');
        }
      }

      // Life Hack details
      if (layout?.lifeHack?.enabled) {
        layoutDetails?.push('Life Hack Page');
      }
    }

    return layoutDetails?.length > 0 ? layoutDetails?.join(', ') : 'Not selected';
  };

  // Load available fonts from database on component mount
  useEffect(() => {
    const loadFonts = async () => {
      try {
        const fonts = await fontService?.getAllFontsForWizard();
        setAvailableFonts(fonts);
      } catch (error) {
        console.error('Failed to load fonts:', error);
      }
    };
    loadFonts();
  }, []);

  // Initialize background on component mount
  useEffect(() => {
    setBackgroundImage(getSessionBackground());
  }, []);

  // Load saved data from Supabase on mount - WAIT for auth to initialize
  useEffect(() => {
    if (!authLoading && user) {
      loadLatestConfig();
    } else if (!authLoading && !user) {
      // User is not authenticated after auth loaded - redirect to login
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load latest configuration from database
  const loadLatestConfig = async () => {
    setIsLoading(true);
    try {
      const latestConfig = await plannerConfigService?.getLatest();

      if (latestConfig) {
        // Extract configuration data from database
        const configData = latestConfig?.configurationData;

        setFormData({
          plannerType: latestConfig?.layoutType || '',
          size: latestConfig?.pageSize || '',
          theme: latestConfig?.colorScheme || '',
          layout: latestConfig?.layoutType || '',
          timeFormat: configData?.timeFormat || '12h-long',
          timeRange: configData?.timeRange || { start: 6, end: 18 },
          sections: configData?.sections || [],
          habits: configData?.habits || [],
          personalization: {
            name: configData?.personalization?.name || '',
            year: configData?.personalization?.year || new Date()?.getFullYear(),
            quote: configData?.personalization?.quote || '',
            font: configData?.personalization?.font || 'roboto',
            titleFont: configData?.personalization?.titleFont || null,
            backgroundText: configData?.personalization?.backgroundText || '',
            coverBackgroundImage: configData?.personalization?.coverBackgroundImage || null, // NEW: Load saved background
            includeCover: configData?.personalization?.includeCover ?? true,
            includeCalendar: configData?.personalization?.includeCalendar ?? true,
            includeGoals: configData?.personalization?.includeGoals ?? false,
            includePageNumbers: configData?.personalization?.includePageNumbers ?? true,
            includeIndex: configData?.personalization?.includeIndex ?? false,
            includeBottomQuotes: configData?.personalization?.includeBottomQuotes ?? false,
            quoteFrequency: configData?.personalization?.quoteFrequency || 'daily',
            selectedQuoteSet: configData?.personalization?.selectedQuoteSet || 'motivational'
          }
        });

        setCurrentConfigId(latestConfig?.id);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      // Continue with empty form if loading fails
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-save configuration to Supabase whenever formData changes
  useEffect(() => {
    if (user && formData?.plannerType) {
      const saveTimer = setTimeout(() => {
        autoSaveConfiguration();
      }, 2000); // Debounce: save 2 seconds after last change

      return () => clearTimeout(saveTimer);
    }
  }, [formData, user]);

  // Auto-save function
  const autoSaveConfiguration = async () => {
    if (!user) return;

    try {
      setIsSaving(true);

      const configData = {
        plannerName: `${formData?.personalization?.name || 'My'} ${formData?.plannerType || 'Planner'} ${formData?.personalization?.year || ''}`?.trim(),
        pageSize: formData?.size || 'A4',
        layoutType: formData?.layout || formData?.plannerType || 'weekly',
        colorScheme: formData?.theme || 'default',
        isFavorite: false,
        configurationData: {
          timeFormat: formData?.timeFormat,
          timeRange: formData?.timeRange,
          sections: formData?.sections,
          habits: formData?.habits,
          personalization: formData?.personalization
        }
      };

      if (currentConfigId) {
        // Update existing configuration
        await plannerConfigService?.update(currentConfigId, configData);
      } else {
        // Create new configuration
        const newConfig = await plannerConfigService?.create(configData);
        setCurrentConfigId(newConfig?.id);
      }
    } catch (error) {
      console.error('Error auto-saving configuration:', error);
      // Don't show error to user for auto-save failures
    } finally {
      setIsSaving(false);
    }
  };

  // Handle preview generation on step 4
  const handleGeneratePreview = async () => {
    if (!validateStep(4)) return;

    setIsGeneratingPreview(true);
    setPreviewError(null);

    // 60 second timeout
    const timeoutId = setTimeout(() => {
      setIsGeneratingPreview(false);
      setPreviewError('Preview generation timed out. Please try again.');
      setCurrentStep(5);
    }, 60000);

    try {
      // Ensure configuration is saved
      const configData = {
        plannerName: `${formData?.personalization?.name || 'My'} ${formData?.plannerType || 'Planner'} ${formData?.personalization?.year || ''}`?.trim(),
        pageSize: formData?.size || 'A4',
        layoutType: formData?.layout || formData?.plannerType || 'weekly',
        colorScheme: formData?.theme || 'default',
        isFavorite: false,
        configurationData: {
          timeFormat: formData?.timeFormat,
          sections: formData?.sections,
          personalization: formData?.personalization
        }
      };

      let savedConfig;
      if (currentConfigId) {
        savedConfig = await plannerConfigService?.update(currentConfigId, configData);
      } else {
        savedConfig = await plannerConfigService?.create(configData);
        setCurrentConfigId(savedConfig?.id);
      }

      // Generate preview PDF (6 pages: title, year, month, week, daily schedule, daily journal)
      const pdfBlob = await generatePlannerPDF({
        plannerData: formData,
        specifications: {
          size: formData?.size,
          theme: formData?.theme,
          type: formData?.plannerType
        },
        includeYearView: true,
        mode: 'preview'
      });

      // Create blob URL for iframe display
      const pdfUrl = URL?.createObjectURL(pdfBlob?.blob);
      setPreviewPdfUrl(pdfUrl);
      setPreviewGenerated(true);

      clearTimeout(timeoutId);
      setIsGeneratingPreview(false);

      // Auto-advance to step 5
      setCurrentStep(5);

    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Preview generation error:', error);
      setPreviewError(error?.message || 'Failed to generate preview. Please try again.');
      setIsGeneratingPreview(false);

      // Still advance to step 5 to show error
      setCurrentStep(5);
    }
  };

  // Handle full planner generation
  const handleGenerateFullPlanner = async () => {
    if (!validateStep(5)) return;

    setIsGeneratingFull(true);
    setShowGenerationModal(true); // Show modal with live log feed
    setStorageUploadStatus({
      inProgress: false,
      success: false,
      error: null,
      storageUrl: null
    });

    // 5 minute timeout for full PDF generation
    const timeoutId = setTimeout(() => {
      setIsGeneratingFull(false);
      setErrors({ generate: 'Full planner generation timed out. Please try again.' });
    }, 300000); // 5 minutes

    try {
      // ALWAYS CREATE NEW ROW - never update existing configuration
      const configData = {
        plannerName: `${formData?.personalization?.name || 'My'} ${formData?.plannerType || 'Planner'} ${formData?.personalization?.year || ''}`?.trim(),
        pageSize: formData?.size || 'A4',
        layoutType: formData?.layout || formData?.plannerType || 'weekly',
        colorScheme: formData?.theme || 'default',
        isFavorite: false,
        isFullPlanner: true, // CRITICAL: Mark as full planner
        configurationData: {
          timeFormat: formData?.timeFormat,
          timeRange: formData?.timeRange,
          sections: formData?.sections,
          habits: formData?.habits,
          personalization: formData?.personalization
        }
      };

      // ALWAYS create new configuration - never update
      let savedConfig = await plannerConfigService?.create(configData);

      // Update currentConfigId to the new configuration
      setCurrentConfigId(savedConfig?.id);

      // Generate full PDF (all pages)
      const pdfResult = await generatePlannerPDF({
        plannerData: formData,
        specifications: {
          size: formData?.size,
          theme: formData?.theme,
          type: formData?.plannerType
        },
        includeYearView: true,
        mode: 'full'
      });

      // Cleanup previous blob URL if exists
      if (fullPdfUrl) {
        URL?.revokeObjectURL(fullPdfUrl);
      }

      clearTimeout(timeoutId);
      setIsGeneratingFull(false);

      // Create blob URL for immediate download fallback
      const blobUrl = URL?.createObjectURL(pdfResult?.blob);
      setFullPdfUrl(blobUrl);
      setFullPdfGenerated(true);

      // Keep modal open for 2 seconds after completion to show 100% progress
      setTimeout(() => {
        setShowGenerationModal(false);
      }, 2000);

      // Auto-advance to step 6
      setCurrentStep(6);

      // Upload to storage bucket in background (non-blocking)
      setStorageUploadStatus({ inProgress: true, success: false, error: null, storageUrl: null });

      try {
        const uploadResult = await plannerStorageService?.uploadFullPlanner(
          pdfResult?.blob,
          formData,
          savedConfig?.id
        );

        if (uploadResult?.success && uploadResult?.storageUrl) {
          setStorageUploadStatus({
            inProgress: false,
            success: true,
            error: null,
            storageUrl: uploadResult?.storageUrl
          });
        } else {
          // Upload failed but blob URL still available
          setStorageUploadStatus({
            inProgress: false,
            success: false,
            error: uploadResult?.uploadError || 'Storage upload failed. Using direct download instead.',
            storageUrl: null
          });
        }
      } catch (uploadError) {
        console.error('Storage upload error:', uploadError);
        setStorageUploadStatus({
          inProgress: false,
          success: false,
          error: 'Storage upload failed. Using direct download instead.',
          storageUrl: null
        });
      }

    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Full planner generation error:', error);
      setErrors({ generate: error?.message || 'Failed to generate full planner. Please try again.' });
      setIsGeneratingFull(false);
      setShowGenerationModal(false);
    }
  };

  // Reset preview and cleanup blob URL when returning to step 4
  useEffect(() => {
    if (currentStep === 4) {
      setPreviewGenerated(false);
      setPreviewError(null);

      // Cleanup previous blob URL to prevent memory leaks
      if (previewPdfUrl) {
        URL?.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
      }
    }
  }, [currentStep]);

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        URL?.revokeObjectURL(previewPdfUrl);
      }
      if (fullPdfUrl) {
        URL?.revokeObjectURL(fullPdfUrl);
      }
    };
  }, [previewPdfUrl, fullPdfUrl]);

  // Update URL when step changes
  useEffect(() => {
    const newUrl = `/planner-setup-wizard?step=${currentStep}`;
    window.history?.replaceState(null, '', newUrl);
  }, [currentStep]);

  const steps = [
  { id: 1, label: 'Type', description: 'Choose planner type' },
  { id: 2, label: 'Format', description: 'Size and theme' },
  { id: 3, label: 'Layout', description: 'Customize layout' },
  { id: 4, label: 'Personal', description: 'Personalization' },
  { id: 5, label: 'Review', description: 'Final review' },
  { id: 6, label: 'Download', description: 'Download planner' }];


  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.plannerType) {
          newErrors.plannerType = 'Please select a planner type';
        }
        break;
      case 2:
        if (!formData?.size) {
          newErrors.size = 'Please select a size format';
        }
        if (!formData?.theme) {
          newErrors.theme = 'Please select a color theme';
        }
        break;
      case 3:
        if (!formData?.layout) {
          newErrors.layout = 'Please select a layout style';
        }
        break;
      case 4:
        if (!formData?.personalization?.name?.trim()) {
          newErrors.name = 'Please enter your name';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle navigation with validation
  const handleNext = () => {
    // STEP 3 VALIDATION: Journal configuration
    if (currentStep === 3) {
      // Check if journal is enabled with subject headings
      if (formData?.layout?.dailyPlanner?.journal?.enabled &&
      formData?.layout?.dailyPlanner?.journal?.type === 'subject_headings') {

        const headings = formData?.layout?.dailyPlanner?.journal?.headings || [];
        const filledHeadings = headings?.filter((h) => h?.trim()?.length > 0);

        // FIXED: Validate minimum 2 headings with text
        if (filledHeadings?.length < 2) {
          alert('Please enter at least 2 subject headings for the journal page before proceeding.');
          return; // Block advancement
        }

        // Check character limits
        const hasOverLimit = headings?.some((h) => h?.length > 75);
        if (hasOverLimit) {
          alert('Subject headings must be less than 75 characters. Please shorten them before proceeding.');
          return; // Block advancement
        }
      }

      // FIXED: Validate step 3 before advancing
      if (!validateStep(3)) return;

      // Advance to step 4 (Personalization)
      setCurrentStep(4);
      return;
    }

    // STEP 4: Generate preview and auto-advance to step 5
    if (currentStep === 4) {
      handleGeneratePreview(); // This will auto-advance to step 5 when complete
      return;
    }

    // STEP 5: Generate full planner and auto-advance to step 6
    if (currentStep === 5) {
      handleGenerateFullPlanner(); // This will auto-advance to step 6 when complete
      return;
    }

    // STEPS 1-2: Simple validation and increment
    if (currentStep < 3) {
      if (!validateStep(currentStep)) return;
      setCurrentStep(currentStep + 1);
      return;
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleStepClick = (step) => {
    if (step <= currentStep || validateStep(currentStep)) {
      setCurrentStep(step);
      setErrors({});
    }
  };

  const handleComplete = async () => {
    if (!validateStep(currentStep)) return;

    setIsLoading(true);

    try {
      // Save final configuration to database
      const configData = {
        plannerName: `${formData?.personalization?.name || 'My'} ${formData?.plannerType || 'Planner'} ${formData?.personalization?.year || ''}`?.trim(),
        pageSize: formData?.size,
        layoutType: formData?.layout,
        colorScheme: formData?.theme,
        isFavorite: false,
        configurationData: {
          timeFormat: formData?.timeFormat,
          sections: formData?.sections,
          personalization: formData?.personalization
        }
      };

      let savedConfig;
      if (currentConfigId) {
        savedConfig = await plannerConfigService?.update(currentConfigId, configData);
      } else {
        savedConfig = await plannerConfigService?.create(configData);
      }

      // Navigate to preview page with configuration ID
      navigate('/planner-preview', {
        state: {
          plannerData: formData,
          configId: savedConfig?.id
        }
      });
    } catch (error) {
      console.error('Error saving planner:', error);
      setErrors({ submit: error?.message || 'Failed to save planner. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear related errors
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSectionToggle = (sectionId, checked) => {
    setFormData((prev) => ({
      ...prev,
      sections: checked ?
      [...prev?.sections, sectionId] :
      prev?.sections?.filter((id) => id !== sectionId)
    }));
  };

  // Add this function for Header onLogout prop
  const handleLogout = () => {
    // Navigation handled by AuthContext
    navigate('/login');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Choose Your Planner Type
              </h2>
              <p className="font-body text-[rgba(203,213,224,0.502)] text-base">
                Select the type of planner that best fits your planning style and needs
              </p>
            </div>
            <PlannerTypeSelector
              selectedType={formData?.plannerType}
              onTypeSelect={(type) => updateFormData('plannerType', type)} />

            {errors?.plannerType &&
            <div className="flex items-center space-x-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.plannerType}</span>
              </div>
            }
          </div>);


      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Size &amp; Theme
              </h2>
              <p className="font-body text-muted-foreground">
                Choose your preferred size format and color theme
              </p>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Size Format
                </h3>
                <SizeFormatSelector
                  selectedSize={formData?.size}
                  onSizeSelect={(size) => updateFormData('size', size)} />

                {errors?.size &&
                <div className="flex items-center space-x-2 text-error text-sm mt-2">
                    <Icon name="AlertCircle" size={16} />
                    <span>{errors?.size}</span>
                  </div>
                }
              </div>
              
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Color Theme
                </h3>
                <ColorThemeSelector
                  selectedTheme={formData?.theme}
                  onThemeSelect={(theme) => updateFormData('theme', theme)} />

                {errors?.theme &&
                <div className="flex items-center space-x-2 text-error text-sm mt-2">
                    <Icon name="AlertCircle" size={16} />
                    <span>{errors?.theme}</span>
                  </div>
                }
              </div>
            </div>
          </div>);


      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Customize Layout
              </h2>
              <p className="font-body text-muted-foreground">
                Design your planner layout and select additional sections
              </p>
            </div>
            <LayoutCustomizer
              selectedLayout={formData?.layout}
              onLayoutSelect={(layout) => updateFormData('layout', layout)}
              selectedSections={formData?.sections}
              onSectionToggle={handleSectionToggle}
              timeFormat={formData?.timeFormat}
              onTimeFormatChange={(format) => updateFormData('timeFormat', format)}
              timeRange={formData?.timeRange}
              onTimeRangeChange={(range) => updateFormData('timeRange', range)}
              selectedHabits={formData?.habits}
              onHabitsChange={(habits) => updateFormData('habits', habits)} />

            {errors?.layout &&
            <div className="flex items-center space-x-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.layout}</span>
              </div>
            }
          </div>);


      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Personalization
              </h2>
              <p className="font-body text-muted-foreground">
                Add your personal touch to make the planner uniquely yours
              </p>
            </div>
            <PersonalizationOptions
              personalization={formData?.personalization}
              onPersonalizationChange={(personalization) =>
              updateFormData('personalization', personalization)
              } />

            {errors?.name &&
            <div className="flex items-center space-x-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.name}</span>
              </div>
            }
            
            {/* Generate Preview Button */}
            <div className="mt-8">
              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={handleGeneratePreview}
                loading={isGeneratingPreview}
                disabled={isGeneratingPreview || !formData?.personalization?.name}
                iconName="FileText"
                iconPosition="left">

                {isGeneratingPreview ? 'Generating Preview...' : 'Generate My Planner'}
              </Button>
            </div>
          </div>);


      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Preview Your Planner
              </h2>
              <p className="font-body text-muted-foreground">
                Review the sample PDF before generating your full planner
              </p>
            </div>
            
            {/* Error Message */}
            {previewError &&
            <div className="bg-error/10 border border-error rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-error mb-1">
                      Preview Generation Failed
                    </h3>
                    <p className="text-sm text-error/80">
                      {previewError}
                    </p>
                  </div>
                </div>
              </div>
            }
            
            {/* PDF Preview with react-pdf */}
            {previewGenerated && previewPdfUrl &&
            <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 bg-muted border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      Preview Sample (6 Pages)
                    </h3>
                    <a
                    href={previewPdfUrl}
                    download="planner-preview.pdf"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">

                      <Icon name="Download" size={16} />
                      <span className="text-sm font-medium">Download Preview</span>
                    </a>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Showing sample pages: Title, Year Overview, Month, Week, Daily Schedule, and Daily Journal
                  </p>
                </div>
                
                {/* Scrollable PDF Viewer Container */}
                <div className="relative bg-gray-100" style={{ height: '600px', overflowY: 'auto', overflowX: 'hidden' }}>
                  <Document
                  file={previewPdfUrl}
                  onLoadSuccess={({ numPages: nextNumPages }) => {
                    setNumPages(nextNumPages);
                    setPdfLoadError(null);
                  }}
                  onLoadError={(error) => {
                    console.error('PDF load error:', error);
                    setPdfLoadError('Your PDF failed to load. Try using download link above');
                  }}
                  loading={
                  <div className="flex flex-col items-center justify-center h-full space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        <p className="text-muted-foreground font-body">Loading PDF preview...</p>
                      </div>
                  }
                  error={
                  <div className="flex flex-col items-center justify-center h-full space-y-4 p-8">
                        <Icon name="AlertCircle" size={48} className="text-error" />
                        <div className="text-center space-y-2">
                          <h3 className="font-heading text-lg font-semibold text-error">
                            PDF Failed to Load
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-md">
                            {pdfLoadError || 'Your PDF failed to load. Try using download link above'}
                          </p>
                          <a
                        href={previewPdfUrl}
                        download="planner-preview.pdf"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors mt-4">

                            <Icon name="Download" size={16} />
                            <span className="text-sm font-medium">Download Instead</span>
                          </a>
                        </div>
                      </div>
                  }
                  className="flex flex-col items-center space-y-4 p-4">

                    {numPages && Array.from(new Array(numPages), (el, index) =>
                  <div
                    key={`page_${index + 1}`}
                    className="bg-white shadow-lg rounded-lg overflow-hidden border-2 border-border"
                    style={{ marginBottom: '16px' }}>

                        <Page
                      pageNumber={index + 1}
                      width={800}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      className="mx-auto" />

                      </div>
                  )}
                  </Document>
                </div>
              </div>
            }
            
            {/* Configuration Summary and Action Buttons */}
            <div className="max-w-2xl mx-auto space-y-6 mt-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Configuration Summary
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Type:</span>
                    <span className="font-medium text-foreground capitalize">
                      {getTypeSummary(formData?.layout)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Size:</span>
                    <span className="font-medium text-foreground uppercase">
                      {safeStringValue(formData?.size) || 'Not selected'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Theme:</span>
                    <span className="font-medium text-foreground capitalize">
                      {safeStringValue(formData?.theme)?.replace(/-/g, ' ') || 'Not selected'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Layout:</span>
                    <span className="font-medium text-foreground capitalize">
                      {getLayoutSummary(formData?.layout)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Sections:</span>
                    <span className="font-medium text-foreground">
                      {formData?.sections?.length || 0} selected
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="font-body text-muted-foreground">Name:</span>
                    <span className="font-medium text-foreground">
                      {safeStringValue(formData?.personalization?.name) || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleGenerateFullPlanner}
                  loading={isGeneratingFull}
                  iconName="FileText"
                  iconPosition="left"
                  disabled={!previewGenerated || !!previewError || isGeneratingFull}>

                  {isGeneratingFull ? 'Generating Full Planner...' : 'Generate Full Planner'}
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={() => setCurrentStep(4)}
                  iconName="Edit"
                  iconPosition="left"
                  disabled={isGeneratingFull}>

                  Make Changes
                </Button>
              </div>
            </div>
            {errors?.generate &&
            <div className="flex items-center space-x-2 text-error text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.generate}</span>
              </div>
            }
          </div>);


      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                Your Planner is Ready!
              </h2>
              <p className="font-body text-muted-foreground">
                Your custom planner has been generated successfully and is ready to download
              </p>
            </div>
            
            {/* Success Icon */}
            <div className="flex justify-center py-8">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Check" size={48} className="text-primary" />
              </div>
            </div>
            
            {/* Storage Upload Status */}
            {storageUploadStatus?.inProgress &&
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 font-medium">
                      Uploading to cloud storage...
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      You can download now while upload continues in background
                    </p>
                  </div>
                </div>
              </div>
            }
            
            {storageUploadStatus?.error &&
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-yellow-800 mb-1">
                      Cloud Storage Unavailable
                    </h3>
                    <p className="text-sm text-yellow-700">
                      {storageUploadStatus?.error}
                    </p>
                    <p className="text-xs text-yellow-600 mt-2">
                      Your planner is still available for direct download below.
                    </p>
                  </div>
                </div>
              </div>
            }
            
            {storageUploadStatus?.success &&
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-green-800 mb-1">
                      Saved to Cloud Storage
                    </h3>
                    <p className="text-sm text-green-700">
                      Your planner has been saved to cloud storage and is available for download anytime from your dashboard.
                    </p>
                  </div>
                </div>
              </div>
            }
            
            {/* Download Section */}
            {fullPdfGenerated && fullPdfUrl &&
            <div className="bg-card border border-border rounded-lg p-8 text-center space-y-6">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    Download Your Planner
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your complete planner with all {formData?.personalization?.year} pages is ready
                  </p>
                </div>
                
                <div className="space-y-3">
                  {/* Primary Download Button - Always available (blob URL) */}
                  <a
                  href={fullPdfUrl}
                  download={`${formData?.personalization?.name?.replace(/\s+/g, '-')}-Planner-${formData?.personalization?.year}.pdf`}
                  className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-lg font-semibold w-full">

                    <Icon name="Download" size={24} />
                    <span>Download Full Planner</span>
                  </a>
                  
                  {/* Storage URL Download - Only show if storage upload succeeded */}
                  {storageUploadStatus?.success && storageUploadStatus?.storageUrl &&
                <a
                  href={storageUploadStatus?.storageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors text-sm font-medium w-full">

                      <Icon name="Cloud" size={20} />
                      <span>View in Cloud Storage</span>
                    </a>
                }
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Click the button above to download your personalized planner
                </p>
              </div>
            }
            
            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-8">
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => {
                  // Reset state and start new planner
                  setCurrentStep(1);
                  setCurrentConfigId(null);
                  setFullPdfGenerated(false);
                  setStorageUploadStatus({
                    inProgress: false,
                    success: false,
                    error: null,
                    storageUrl: null
                  });
                  if (fullPdfUrl) {
                    URL?.revokeObjectURL(fullPdfUrl);
                    setFullPdfUrl(null);
                  }
                  if (previewPdfUrl) {
                    URL?.revokeObjectURL(previewPdfUrl);
                    setPreviewPdfUrl(null);
                  }
                  setPreviewGenerated(false);
                  setFormData({
                    plannerType: '',
                    size: '',
                    theme: '',
                    layout: '',
                    timeFormat: '12h-long',
                    timeRange: { start: 6, end: 18 },
                    sections: [],
                    habits: [],
                    personalization: {
                      name: '',
                      year: new Date()?.getFullYear(),
                      quote: '',
                      font: 'roboto',
                      titleFont: null,
                      backgroundText: '',
                      coverBackgroundImage: null,
                      includeCover: true,
                      includeCalendar: true,
                      includeGoals: false,
                      includePageNumbers: true,
                      includeIndex: false,
                      includeBottomQuotes: false,
                      quoteFrequency: 'daily',
                      selectedQuoteSet: 'motivational'
                    }
                  });
                }}
                iconName="Plus"
                iconPosition="left">

                Create Another Planner
              </Button>
              
              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={() => navigate('/user-account-dashboard')}
                iconName="Home"
                iconPosition="left">

                Go to Dashboard
              </Button>
            </div>
          </div>);


      default:
        return null;
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {authLoading ? 'Authenticating...' : 'Loading your planner...'}
          </p>
        </div>
      </div>);

  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Random rotation per session */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("${backgroundImage}")`
        }}>

        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content wrapper with relative positioning */}
      <div className="relative min-h-screen">
        <Header onLogout={handleLogout} />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Auto-save indicator */}
            {isSaving &&
            <div className="mb-4 flex items-center justify-end text-sm text-white/90 drop-shadow">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span>Saving...</span>
              </div>
            }

            {/* Progress Indicator - REMOVED glassmorphism */}
            <div className="mb-8">
              <div className="bg-white/95 border border-gray-200 rounded-lg p-4">
                <ProgressIndicator
                  currentStep={currentStep}
                  totalSteps={steps?.length}
                  steps={steps}
                  onStepClick={handleStepClick} />

              </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
              {/* Form Content - REMOVED glassmorphism */}
              <div className="xl:col-span-3">
                <div className="bg-white/95 border border-gray-200 rounded-lg p-6 lg:p-8">
                  {renderStepContent()}
                </div>
              </div>

              {/* Sidebar Preview (Desktop) - REMOVED glassmorphism */}
              <div className="hidden xl:block">
                <div className="sticky top-24">
                  <div className="bg-white/95 border border-gray-200 rounded-lg p-4">
                    <PlannerPreview
                      plannerType={safeStringValue(formData?.plannerType)}
                      size={safeStringValue(formData?.size)}
                      theme={safeStringValue(formData?.theme)}
                      layout={formData?.layout}
                      sections={formData?.sections || []}
                      personalization={{
                        ...formData?.personalization,
                        name: safeStringValue(formData?.personalization?.name),
                        quote: safeStringValue(formData?.personalization?.quote),
                        font: safeStringValue(formData?.personalization?.font),
                        quoteFrequency: safeStringValue(formData?.personalization?.quoteFrequency),
                        selectedQuoteSet: safeStringValue(formData?.personalization?.selectedQuoteSet)
                      }} />

                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Controls - REMOVED glassmorphism */}
            {currentStep < 6 &&
            <div className="mt-8 bg-white/95 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1 || isGeneratingPreview || isGeneratingFull}
                  iconName="ArrowLeft"
                  iconPosition="left">

                    Previous
                  </Button>

                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <span>Step {currentStep} of {steps?.length}</span>
                  </div>

                  <Button
                  variant="default"
                  onClick={handleNext}
                  disabled={isGeneratingPreview || isGeneratingFull}
                  iconName="ArrowRight"
                  iconPosition="right">

                    {currentStep === 3 ? 'Next' : currentStep === 4 ? 'Generate Preview' : currentStep === 5 ? 'Review' : 'Next'}
                  </Button>
                </div>
              </div>
            }
          </div>
        </main>
      </div>
      
      {/* Generation Modal */}
      <PlannerGenerationModal
        isOpen={showGenerationModal}
        onClose={() => {
          if (!isGeneratingFull) {
            setShowGenerationModal(false);
          }
        }}
        isGenerating={isGeneratingFull} />

    </div>);

};

export default PlannerSetupWizard;